import {
  useCompany,
  useFnolRequiredClaimAttributes,
} from '@dispatch/shared/hooks';
import type {
  AttributeOptions,
  FNOL_RequestForm,
  IngressRequestForm,
} from '@dispatch/shared/types';
import {
  buildQualifiedFnolRequestFormSchema,
  buildQualifiedIngressRequestFormSchema,
} from '@dispatch/shared/types';
import { useCurrentUser } from '@dispatch/web/auth';
import { useNotification } from '@dispatch/web/hooks';
import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect } from 'react';
import type {
  Resolver,
  UseFormGetValues,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import {
  buildMissingPinnedRows,
  hasAllPinnedRows,
  resolvePinnedAttributes,
} from './pinnedClaimAttributes';

const NO_PINNED_GUIDS: string[] = [];

export type PinnedClaimAttributes = {
  /** undefined until the configuration and the company attributes are known */
  pinnedAttributes: AttributeOptions[] | undefined;
  pinnedGuids: string[] | undefined;
};

const isCompanyPending = (companyStatus: string | undefined): boolean =>
  companyStatus === 'idle' || companyStatus === 'loading';

/**
 * Resolves the tenant's configured required claim attributes against the
 * company attributes. Unresolved while the configuration loads, or while it
 * names attributes and the company read is still in flight. If the company
 * read ends without attributes, nothing is pinned and the user is notified.
 */
export const usePinnedClaimAttributes = (
  companyAttributes: AttributeOptions[]
): PinnedClaimAttributes => {
  const { tenantId } = useCurrentUser();
  const { companyStatus } = useCompany();
  const { notifyError } = useNotification();
  const { attributeNames, isLoading } =
    useFnolRequiredClaimAttributes(tenantId);

  const needsCompanyAttributes =
    attributeNames.length > 0 && companyAttributes.length === 0;
  const isUnresolved =
    isLoading || (needsCompanyAttributes && isCompanyPending(companyStatus));
  const hasNoCompanyAttributes = !isUnresolved && needsCompanyAttributes;

  useEffect(() => {
    if (hasNoCompanyAttributes) {
      notifyError(
        'Required claim attributes unavailable',
        'Company attributes could not be loaded, so claim attributes are not required for this referral.'
      );
    }
    // Notify once per transition into the fail-open state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasNoCompanyAttributes]);

  const pinnedAttributes = isUnresolved
    ? undefined
    : resolvePinnedAttributes(attributeNames, companyAttributes);

  return {
    pinnedAttributes,
    pinnedGuids: pinnedAttributes?.map((attribute) => attribute.value),
  };
};

export type QualifiedFormResolvers = {
  fnolResolver: Resolver<FNOL_RequestForm>;
  ingressResolver: Resolver<IngressRequestForm>;
};

export const buildQualifiedFormResolvers = (
  pinnedGuids: string[] | undefined,
  isDolLocked: boolean
): QualifiedFormResolvers => {
  const guids = pinnedGuids ?? NO_PINNED_GUIDS;
  return {
    fnolResolver: yupResolver(
      buildQualifiedFnolRequestFormSchema({
        pinnedGuids: guids,
        isDolLocked,
      })
    ),
    ingressResolver: yupResolver(
      buildQualifiedIngressRequestFormSchema({ pinnedGuids: guids })
    ),
  };
};

type SeedPinnedRowsParams = {
  pinned: PinnedClaimAttributes;
  getValues: UseFormGetValues<FNOL_RequestForm>;
  setValue: UseFormSetValue<FNOL_RequestForm>;
  watch: UseFormWatch<FNOL_RequestForm>;
};

/**
 * Prepends a row for every pinned attribute missing from claimAttributes,
 * keeping the rows already there. Seeds with setValue because defaultValues
 * are read only once, before the configuration has loaded.
 *
 * @returns true once every pinned attribute has a row
 */
export const useSeedPinnedClaimAttributeRows = ({
  pinned: { pinnedAttributes, pinnedGuids },
  getValues,
  setValue,
  watch,
}: SeedPinnedRowsParams): boolean => {
  const pinnedGuidsKey = pinnedGuids?.join('\n');

  useEffect(() => {
    if (!pinnedAttributes || pinnedAttributes.length === 0) return;

    const rows = getValues('claimAttributes') ?? [];
    const missing = buildMissingPinnedRows(rows, pinnedAttributes);
    if (missing.length === 0) return;

    setValue('claimAttributes', [...missing, ...rows]);
    // The key identifies pinnedAttributes, which is rebuilt every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pinnedGuidsKey]);

  const rows = watch('claimAttributes');

  return pinnedGuids !== undefined && hasAllPinnedRows(rows, pinnedGuids);
};
